/**
 * canvas-engine/presets/decompositionAdapters/buildSectionCanvas.js
 *
 * OWN core decomposition engine (Task 3.2). Replaces the legacy `generic.js`.
 *
 * ZERO-REUSE CONSTRAINT: this module imports NOTHING from
 * `admin-console-ui/src/modules/template-builder/` or `academy-ui/src/template/`.
 * It MAY import its own engine siblings (`../../model/*`) and the transcribed
 * `../presetData/*` VALUES only.
 *
 * Pure module — no React, no DOM, no side effects, no `Math.random`/clock use.
 * Given the same `sectionData` (a transcribed preset DATA descriptor) and the
 * same `options`, it ALWAYS produces the same normalized `Canvas_Model`
 * (deterministic; stable element ids derived from `sectionType` + ordinal).
 *
 * What it does (design: "Section → Canvas decomposition" / "How positions/
 * styles are derived at the fixed Reference_Width"):
 *   1. Derives the frame style (background, radius, shadow, padding, optional
 *      backgroundImage) from the section's `style`.
 *   2. Lays the section's `content` out top-to-bottom in a deterministic
 *      vertical stack within the fixed `Reference_Width` (1120) content frame:
 *        - lead text block (eyebrow/heading/subheading/body)  → `text`
 *        - named title/description pairs (mission/vision/...)  → `text` (+`html`)
 *        - pull quote                                          → `text`
 *        - authored HTML (body/description/html)               → `html`
 *        - a primary image / image gallery                     → `image`/`imageGrid`
 *        - an action row of buttons                            → `button`[]
 *        - repeated blocks/cards/items                         → `grid`
 *        - an optional dynamic region (binding marker)         → `dataBound`
 *        - a full-frame background image when present          → `image` (z=0)
 *   3. Maps colors to element `style[...]` color keys (CSS strings, Req 22.3)
 *      and images to `{ storedLink, displayLink }` assets (Req 22.4).
 *   4. NEVER emits a `native` element; `normalizeCanvas` enforces
 *      `layoutMode: "absolute"` (Req 3.5, 4.4).
 *   5. Records lossless provenance under `canvas.source.preserved`
 *      (`{ type, preserved: { content, style, layout } }`) for traceability and
 *      fallback (design: "Lossless provenance recorded under
 *      canvas.source.preserved").
 *
 * The exported layout helpers (cursor, padding parser, frame-style deriver,
 * element factories, color/asset mappers) are reused by the family/chrome/
 * custom/dynamic adapters in Task 3.3.
 *
 * Requirements: 3.1, 3.5, 3.6, 4.1, 4.4, 6.3, 21.1
 */

import { REFERENCE_WIDTH, MIN_DIMENSION } from '../../model/schema';
import { normalizeCanvas } from '../../model/canvasModel';
import { normalizeAsset, isEmptyAsset } from '../../model/assetRef';

// ── Layout constants (at the fixed Reference_Width) ─────────────────────────

/** Reference content-frame width (px). All positions are defined against this. */
export const REFERENCE_FRAME_WIDTH = REFERENCE_WIDTH;

/** Default vertical gap between stacked blocks (px). */
export const DEFAULT_STACK_GAP = 32;

/** Default horizontal gap inside the action button row (px). */
export const DEFAULT_BUTTON_GAP = 16;

/** Fallback padding used when a section omits / mis-specifies `style.padding`. */
const FALLBACK_PADDING = Object.freeze({ top: 56, right: 24, bottom: 56, left: 24 });

// ── Small pure value helpers ────────────────────────────────────────────────

/** True for a non-null, non-array plain object. */
function isPlainObject(v) {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Deep clone of any JSON-shaped value. */
function deepClone(v) {
  if (Array.isArray(v)) return v.map(deepClone);
  if (isPlainObject(v)) {
    const out = {};
    const keys = Object.keys(v);
    for (let i = 0; i < keys.length; i += 1) out[keys[i]] = deepClone(v[keys[i]]);
    return out;
  }
  return v;
}

/** Trimmed string, or '' when not a usable string. */
function pickString(v) {
  return typeof v === 'string' ? v.trim() : '';
}

/** First non-empty trimmed string among the candidates, else ''. */
function firstString(candidates) {
  for (let i = 0; i < candidates.length; i += 1) {
    const s = pickString(candidates[i]);
    if (s) return s;
  }
  return '';
}

/** Parse a px-like CSS value (`640px`, `640`, `72px 24px`) into its first number. */
function firstCssNumber(value) {
  if (Number.isFinite(value)) return value;
  if (typeof value !== 'string') return NaN;
  const match = value.trim().match(/-?\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : NaN;
}

/** First non-empty array among the candidates, else []. */
function firstArray(candidates) {
  for (let i = 0; i < candidates.length; i += 1) {
    if (Array.isArray(candidates[i]) && candidates[i].length > 0) return candidates[i];
  }
  return [];
}

/** Heuristic: does this string contain HTML markup? */
export function looksLikeHtml(s) {
  return typeof s === 'string' && /<[a-z!/][\s\S]*>/i.test(s);
}

/** First candidate that looks like HTML, else ''. */
function firstHtml(candidates) {
  for (let i = 0; i < candidates.length; i += 1) {
    if (looksLikeHtml(candidates[i])) return candidates[i];
  }
  return '';
}

/** Strip HTML tags to plain text (for size estimation only). */
function stripTags(s) {
  return typeof s === 'string' ? s.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() : '';
}

/** Clamp a number into [lo, hi]. */
function clamp(n, lo, hi) {
  if (!Number.isFinite(n)) return lo;
  return Math.min(Math.max(n, lo), hi);
}

/** True when an asset-shaped value resolves to a usable link. */
export function isUsableAsset(v) {
  return !isEmptyAsset(normalizeAsset(v));
}

// ── Padding parsing + layout cursor ─────────────────────────────────────────

/**
 * Parse a CSS `padding` shorthand string ("72px 24px", "96px 24px 64px", "0",
 * …) into `{ top, right, bottom, left }` px numbers. Falls back to a sensible
 * default for missing / unparsable input.
 *
 * @param {string} padding
 * @returns {{top:number,right:number,bottom:number,left:number}}
 */
export function parsePadding(padding) {
  if (typeof padding !== 'string' || padding.trim() === '') {
    return { ...FALLBACK_PADDING };
  }
  const parts = padding
    .trim()
    .split(/\s+/)
    .map((p) => {
      const n = parseFloat(p);
      return Number.isFinite(n) ? n : 0;
    });

  let top;
  let right;
  let bottom;
  let left;
  if (parts.length === 1) {
    top = parts[0];
    right = parts[0];
    bottom = parts[0];
    left = parts[0];
  } else if (parts.length === 2) {
    top = parts[0];
    bottom = parts[0];
    right = parts[1];
    left = parts[1];
  } else if (parts.length === 3) {
    top = parts[0];
    right = parts[1];
    left = parts[1];
    bottom = parts[2];
  } else {
    top = parts[0];
    right = parts[1];
    bottom = parts[2];
    left = parts[3];
  }
  return { top, right, bottom, left };
}

/**
 * Create a vertical layout cursor for a frame of `frameWidth` and a CSS
 * `padding` shorthand. The cursor tracks the running `y`, the left margin `x`,
 * and the usable `contentWidth` (= frameWidth - left - right padding).
 *
 * @param {number} frameWidth
 * @param {string} padding CSS padding shorthand
 * @returns {{frameWidth:number,pad:object,x:number,y:number,contentWidth:number,_started:boolean}}
 */
export function createLayoutCursor(frameWidth, padding) {
  const width = Number.isFinite(frameWidth) && frameWidth >= MIN_DIMENSION ? frameWidth : REFERENCE_FRAME_WIDTH;
  const pad = parsePadding(padding);
  const contentWidth = Math.max(width - pad.left - pad.right, MIN_DIMENSION);
  return { frameWidth: width, pad, x: pad.left, y: pad.top, contentWidth, _started: false };
}

/**
 * Reserve a full content-width block of `height` px in the cursor, advancing
 * the cursor past it (inserting `gap` before every block after the first).
 * Returns the placed `{ x, y, width, height }` frame.
 *
 * @param {object} cursor a cursor from `createLayoutCursor`
 * @param {number} height block height (px)
 * @param {number} [gap] gap before this block (px)
 * @returns {{x:number,y:number,width:number,height:number}}
 */
export function placeBlock(cursor, height, gap = DEFAULT_STACK_GAP) {
  const h = Math.max(Number.isFinite(height) ? height : MIN_DIMENSION, MIN_DIMENSION);
  if (cursor._started) cursor.y += gap;
  const placed = { x: cursor.x, y: cursor.y, width: cursor.contentWidth, height: h };
  cursor.y += h;
  cursor._started = true;
  return placed;
}

/** Final frame height implied by the cursor (running y + bottom padding). */
export function cursorFrameHeight(cursor) {
  return Math.max(cursor.y + cursor.pad.bottom, MIN_DIMENSION);
}

// ── Size estimation (deterministic, content-aware) ──────────────────────────

function estimateLines(str, charsPerLine) {
  const s = pickString(str);
  if (!s) return 0;
  return Math.max(1, Math.ceil(s.length / Math.max(charsPerLine, 1)));
}

/** Estimate a text block height from its parts at the given content width. */
export function estimateTextHeight({ eyebrow, heading, body } = {}, width = REFERENCE_FRAME_WIDTH) {
  const headingChars = Math.max(10, Math.floor(width / 20));
  const bodyChars = Math.max(20, Math.floor(width / 9));
  let h = 8;
  if (pickString(eyebrow)) h += 30;
  if (pickString(heading)) h += estimateLines(heading, headingChars) * 46;
  if (pickString(body)) h += estimateLines(body, bodyChars) * 28;
  return Math.max(h, 48);
}

/** Estimate an HTML block height from its (tag-stripped) text length. */
function estimateHtmlHeight(html, width) {
  const text = stripTags(html);
  const bodyChars = Math.max(20, Math.floor(width / 9));
  return clamp(estimateLines(text, bodyChars) * 28 + 40, 120, 900);
}

// ── Color helpers (single CSS-string representation, Req 22.3) ───────────────

/** Relative luminance (0..1) of a #rgb/#rrggbb hex color; null when not hex. */
function hexLuminance(color) {
  if (typeof color !== 'string') return null;
  let hex = color.trim();
  if (hex[0] !== '#') return null;
  hex = hex.slice(1);
  if (hex.length === 3) {
    hex = hex.split('').map((c) => c + c).join('');
  }
  if (hex.length !== 6 || /[^0-9a-f]/i.test(hex)) return null;
  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Choose a readable text color (CSS string) for a given background color.
 * Light backgrounds → dark slate text; dark / non-hex backgrounds → white.
 */
export function contrastColor(background) {
  const lum = hexLuminance(background);
  if (lum === null) return '#ffffff';
  return lum > 0.6 ? '#0f172a' : '#ffffff';
}

/**
 * Map an action `variant` + the section accent color to a `button` element
 * style. Primary fills with the accent; outline/secondary draws the accent as
 * border + label on a transparent fill.
 */
export function mapButtonStyle(variant, accent) {
  const accentColor = pickString(accent) || '#2563eb';
  if (variant === 'outline' || variant === 'secondary' || variant === 'ghost') {
    return {
      background: 'transparent',
      color: accentColor,
      borderColor: accentColor,
    };
  }
  return {
    background: accentColor,
    color: contrastColor(accentColor),
    borderColor: 'transparent',
  };
}

// ── Element factories ────────────────────────────────────────────────────────
//
// Each factory returns a PARTIAL Canvas_Element; `normalizeCanvas` fills in any
// missing per-type defaults, clamps dimensions, and asset-normalizes the
// top-level `content.asset` / `content.image` keys. We always supply a stable
// `id` so normalization preserves it (deterministic).

/** Build a `text` element. */
export function textElement(id, frame, zIndex, content, style) {
  return { id, type: 'text', ...frame, zIndex, content: { ...content }, style: { ...style } };
}

/** Build an `image` element from any asset-shaped value. */
export function imageElement(id, frame, zIndex, asset, style = {}, alt = '') {
  return {
    id,
    type: 'image',
    ...frame,
    zIndex,
    content: { asset: normalizeAsset(asset), alt: pickString(alt) },
    style: { ...style },
  };
}

/** Build a `button` element. */
export function buttonElement(id, frame, zIndex, label, url, style, newTab = false) {
  return {
    id,
    type: 'button',
    ...frame,
    zIndex,
    content: { label: pickString(label) || 'Button', url: pickString(url), linkType: 'url', newTab: !!newTab },
    style: { ...style },
  };
}

/** Build an `html` element. */
export function htmlElement(id, frame, zIndex, html, css = '') {
  return {
    id,
    type: 'html',
    ...frame,
    zIndex,
    content: { html: typeof html === 'string' ? html : '', css: pickString(css) },
    style: {},
  };
}

/** Build a `grid` element from a list of mapped items. */
export function gridElement(id, frame, zIndex, items, columns, gap) {
  return {
    id,
    type: 'grid',
    ...frame,
    zIndex,
    content: { items: Array.isArray(items) ? items : [] },
    style: { imageRadius: 12, objectFit: 'cover' },
    layout: { columns: clamp(columns, 1, 6), gap: Number.isFinite(gap) ? gap : 24 },
  };
}

/** Build an `imageGrid` element from a list of asset-shaped values. */
export function imageGridElement(id, frame, zIndex, images, columns, gap) {
  return {
    id,
    type: 'imageGrid',
    ...frame,
    zIndex,
    content: { images: (Array.isArray(images) ? images : []).map(normalizeAsset) },
    style: { imageRadius: 12, objectFit: 'cover' },
    layout: { columns: clamp(columns, 1, 6), gap: Number.isFinite(gap) ? gap : 24 },
  };
}

/** Build a `dataBound` element carrying a binding marker. */
export function dataBoundElement(id, frame, zIndex, content, binding) {
  return {
    id,
    type: 'dataBound',
    ...frame,
    zIndex,
    content: { ...content },
    binding: { source: pickString(binding && binding.source), params: isPlainObject(binding && binding.params) ? { ...binding.params } : {} },
  };
}

/**
 * Map a transcribed "block"/"card"/"item" descriptor into a `grid` item,
 * normalizing any per-item image (imageContent / imageColumns / imageUrl /
 * image / asset) into the single asset representation. Colors stay CSS strings.
 */
export function mapBlockToItem(block) {
  const b = isPlainObject(block) ? block : {};
  const image = normalizeAsset(b.imageContent || b.imageUrl || b.image || b.asset || b.src || null);
  const item = {
    title: pickString(b.title),
    description: pickString(b.description),
  };
  if (!isEmptyAsset(image)) item.image = image;
  if (pickString(b.accentColor)) item.accentColor = pickString(b.accentColor);
  if (pickString(b.icon)) item.icon = pickString(b.icon);
  if (pickString(b.quote)) item.quote = pickString(b.quote);
  if (pickString(b.url)) item.url = pickString(b.url);
  if (pickString(b.level)) item.level = pickString(b.level);
  if (pickString(b.badge)) item.badge = pickString(b.badge);
  if (pickString(b.price)) item.price = pickString(b.price);
  return item;
}

// ── Named title/value pairs commonly present in transcribed sections ─────────

const NAMED_PAIRS = Object.freeze([
  ['missionTitle', 'missionDescription'],
  ['visionTitle', 'visionDescription'],
  ['deliveryTitle', 'deliveryDescription'],
  ['categoryTitle', null],
  ['scheduleTitle', null],
  ['curriculumTitle', null],
  ['valuesTitle', null],
  ['aboutSectionTitle', null],
  ['imageHeading', 'imageSubtitle'],
  ['phoneTitle', 'phoneValue'],
  ['emailTitle', 'emailValue'],
  ['addressTitle', 'addressValue'],
  ['descriptionTitle', 'description'],
]);

// ── Frame-style derivation ───────────────────────────────────────────────────

/**
 * Derive the Canvas_Model frame style (the keys `normalizeCanvas` understands)
 * from a section `style`. Only includes a key when the section supplies a
 * usable value, so `normalizeCanvas` fills the rest from `FRAME_DEFAULTS`.
 *
 * @param {object} style a section style descriptor
 * @returns {object} a partial frame style ({ background?, radius?, shadow?, padding?, backgroundImage? })
 */
export function deriveFrameStyle(style) {
  const s = isPlainObject(style) ? style : {};
  const frame = {};
  const background = firstString([
    s.bgGradient,
    s.backgroundGradient,
    s.backgroundColor,
    s.bgColor,
    s.background,
  ]);
  if (background) frame.background = background;
  if (pickString(s.padding) || s.padding === '0' || s.padding === 0) {
    frame.padding = typeof s.padding === 'string' ? s.padding : String(s.padding);
  }
  frame.radius = Number.isFinite(s.radius) ? s.radius : 0;
  frame.shadow = pickString(s.shadow) || 'none';
  const backgroundImage = [
    s.backgroundImage,
    s.backgroundImageContent,
    s.bgImage,
    s.bgImageContent,
  ].find((candidate) => candidate != null && isUsableAsset(candidate));
  if (backgroundImage != null) {
    frame.backgroundImage = normalizeAsset(backgroundImage);
  }
  return frame;
}

function resolveAuthoredFrameHeight(style, layout) {
  const s = isPlainObject(style) ? style : {};
  const l = isPlainObject(layout) ? layout : {};
  const sectionBox = isPlainObject(l.sectionBox) ? l.sectionBox : {};
  const candidates = [
    sectionBox.height,
    s.minHeight,
    s.height,
    l.minHeight,
    l.height,
  ]
    .map(firstCssNumber)
    .filter((height) => Number.isFinite(height) && height >= MIN_DIMENSION);
  return candidates.length ? Math.max(...candidates) : 0;
}

// ── The core decomposition engine ────────────────────────────────────────────

/**
 * Decompose a transcribed preset DATA descriptor into a normalized, fully
 * editable `Canvas_Model` at the fixed `Reference_Width`.
 *
 * @param {object} sectionData a preset DATA descriptor:
 *   `{ sectionType, label?, domain?, style?, layout?, content?, binding? }`
 * @param {object} [options]
 *   - `frameWidth` {number}  frame width (default REFERENCE_FRAME_WIDTH)
 *   - `idPrefix`   {string}  stable element id prefix (default sectionType)
 *   - `sectionType`{string}  provenance type override (default sectionData.sectionType)
 * @returns {object} a normalized `Canvas_Model` (layoutMode "absolute", with
 *   `source.preserved` provenance). Never contains a `native` element.
 */
export function buildSectionCanvas(sectionData, options = {}) {
  const data = isPlainObject(sectionData) ? sectionData : {};
  const content = isPlainObject(data.content) ? data.content : {};
  const style = isPlainObject(data.style) ? data.style : {};
  const layout = isPlainObject(data.layout) ? data.layout : {};

  const opts = isPlainObject(options) ? options : {};
  const frameWidth = Number.isFinite(opts.frameWidth) && opts.frameWidth >= MIN_DIMENSION ? opts.frameWidth : REFERENCE_FRAME_WIDTH;
  const sectionType = pickString(opts.sectionType) || pickString(data.sectionType) || 'section';
  const idPrefix = pickString(opts.idPrefix) || sectionType;

  // Additive auth-variant merge (Req 3.5–3.7): when `options.authVariant` names a
  // plain-object variant under `content.variants`, that variant is shallow-merged
  // OVER the resolved content BEFORE layout, so the variant's heading/subheading/
  // formTitle/fields/images replace the signIn defaults. The ORIGINAL `content`
  // (including `variants`) is preserved verbatim under `source.preserved.content`
  // below, so nothing is lost. The `variants` object itself is never laid out as
  // content (no layout read references it).
  const authVariant = pickString(opts.authVariant);
  const effectiveContent =
    authVariant && isPlainObject(content.variants) && isPlainObject(content.variants[authVariant])
      ? { ...content, ...content.variants[authVariant] }
      : content;

  // Deterministic id factory: `${idPrefix}-${kind}-${ordinal}`.
  let idOrdinal = 0;
  const nextId = (kind) => {
    const id = `${idPrefix}-${kind}-${idOrdinal}`;
    idOrdinal += 1;
    return id;
  };

  // Resolved style values reused across blocks.
  const textColor = pickString(style.textColor) || '#0f172a';
  const accentColor = pickString(style.accentColor) || '#2563eb';
  const align = layout.align === 'center' || layout.align === 'right' ? layout.align : 'left';

  const cursor = createLayoutCursor(frameWidth, style.padding);
  const elements = [];
  let zIndex = 1;
  const pushZ = () => {
    const z = zIndex;
    zIndex += 1;
    return z;
  };

  // 1) Lead text block: eyebrow/badge + heading/title + subheading/subtitle.
  const eyebrow = firstString([effectiveContent.eyebrow, effectiveContent.badge]);
  const heading = firstString([effectiveContent.heading, effectiveContent.title]);
  const leadBodyPlain = looksLikeHtml(effectiveContent.body) ? '' : pickString(effectiveContent.body);
  const leadBody = firstString([effectiveContent.subheading, effectiveContent.subtitle, leadBodyPlain]);
  if (eyebrow || heading || leadBody) {
    const frame = placeBlock(cursor, estimateTextHeight({ eyebrow, heading, body: leadBody }, cursor.contentWidth));
    elements.push(
      textElement(nextId('text'), frame, pushZ(), { eyebrow, heading, body: leadBody }, {
        headingColor: textColor,
        bodyColor: textColor,
        eyebrowColor: accentColor,
        textAlign: align,
      }),
    );
  }

  // 2) Named title/description pairs (mission/vision/contact/...).
  for (let i = 0; i < NAMED_PAIRS.length; i += 1) {
    const [titleKey, valueKey] = NAMED_PAIRS[i];
    const title = pickString(effectiveContent[titleKey]);
    const rawValue = valueKey ? effectiveContent[valueKey] : '';
    const hasValue = pickString(rawValue) || looksLikeHtml(rawValue);
    if (!title && !hasValue) continue; // eslint-disable-line no-continue

    if (looksLikeHtml(rawValue)) {
      if (title) {
        const tFrame = placeBlock(cursor, estimateTextHeight({ heading: title }, cursor.contentWidth));
        elements.push(
          textElement(nextId('text'), tFrame, pushZ(), { eyebrow: '', heading: title, body: '' }, {
            headingColor: textColor,
            bodyColor: textColor,
            eyebrowColor: accentColor,
            textAlign: align,
          }),
        );
      }
      const hFrame = placeBlock(cursor, estimateHtmlHeight(rawValue, cursor.contentWidth));
      elements.push(htmlElement(nextId('html'), hFrame, pushZ(), rawValue));
    } else {
      const body = pickString(rawValue);
      const frame = placeBlock(cursor, estimateTextHeight({ heading: title, body }, cursor.contentWidth));
      elements.push(
        textElement(nextId('text'), frame, pushZ(), { eyebrow: '', heading: title, body }, {
          headingColor: textColor,
          bodyColor: textColor,
          eyebrowColor: accentColor,
          textAlign: align,
        }),
      );
    }
  }

  // 3) Pull quote.
  const quote = pickString(effectiveContent.quote);
  if (quote) {
    const author = pickString(effectiveContent.quoteAuthor);
    const quoteBody = author ? `${quote} — ${author}` : quote;
    const frame = placeBlock(cursor, estimateTextHeight({ body: quoteBody }, cursor.contentWidth));
    elements.push(
      textElement(nextId('text'), frame, pushZ(), { eyebrow: '', heading: '', body: quoteBody }, {
        headingColor: textColor,
        bodyColor: accentColor,
        eyebrowColor: accentColor,
        textAlign: align,
        fontStyle: 'italic',
      }),
    );
  }

  // 4) Authored HTML from body/richText (description handled via named pairs).
  const html = firstHtml([effectiveContent.body, effectiveContent.html, effectiveContent.richText]);
  if (html) {
    const frame = placeBlock(cursor, estimateHtmlHeight(html, cursor.contentWidth));
    elements.push(htmlElement(nextId('html'), frame, pushZ(), html, effectiveContent.css));
  }

  // 5) Primary image / image gallery.
  const images = (Array.isArray(effectiveContent.images) ? effectiveContent.images : []).filter(isUsableAsset);
  if (images.length === 1) {
    const frame = placeBlock(cursor, 420);
    elements.push(imageElement(nextId('image'), frame, pushZ(), images[0], { objectFit: 'cover', radius: 16 }, pickString(effectiveContent.imageAlt)));
  } else if (images.length > 1) {
    const cols = clamp(images.length, 2, 3);
    const rows = Math.ceil(images.length / cols);
    const frame = placeBlock(cursor, rows * 240 + (rows - 1) * 16);
    elements.push(imageGridElement(nextId('imageGrid'), frame, pushZ(), images, cols, 16));
  }

  // 6) Action row of buttons (laid horizontally within the content width).
  const actions = Array.isArray(effectiveContent.actions) ? effectiveContent.actions.filter((a) => isPlainObject(a) && pickString(a.label)) : [];
  if (actions.length > 0) {
    const rowFrame = placeBlock(cursor, 56);
    let offsetX = rowFrame.x;
    for (let i = 0; i < actions.length; i += 1) {
      const a = actions[i];
      const label = pickString(a.label) || 'Button';
      const bw = clamp(label.length * 9 + 56, 150, cursor.contentWidth);
      const btnFrame = { x: offsetX, y: rowFrame.y, width: bw, height: 56 };
      elements.push(buttonElement(nextId('button'), btnFrame, pushZ(), label, a.url, mapButtonStyle(a.variant, accentColor), a.newTab));
      offsetX += bw + DEFAULT_BUTTON_GAP;
    }
  }

  // 7) Repeated blocks / cards / items → a single editable grid.
  const collection = firstArray([effectiveContent.cards, effectiveContent.blocks, effectiveContent.items, effectiveContent.highlights]);
  if (collection.length > 0) {
    const columns = clamp(layout.columns || 3, 1, 6);
    const gap = Number.isFinite(layout.gap) ? layout.gap : 24;
    const items = collection.map(mapBlockToItem);
    const hasImage = items.some((it) => it.image && !isEmptyAsset(it.image));
    const rows = Math.ceil(items.length / columns);
    const cardHeight = hasImage ? 340 : 200;
    const gridHeight = rows * cardHeight + Math.max(rows - 1, 0) * gap;
    const frame = placeBlock(cursor, gridHeight);
    elements.push(gridElement(nextId('grid'), frame, pushZ(), items, columns, gap));
  }

  // 8) Optional dynamic region → dataBound element carrying the binding marker.
  const binding = isPlainObject(data.binding) ? data.binding : isPlainObject(effectiveContent.binding) ? effectiveContent.binding : null;
  if (binding && pickString(binding.source)) {
    const params = { ...(isPlainObject(binding.params) ? binding.params : {}) };
    if (binding.limit != null && params.limit == null) params.limit = binding.limit;
    if (pickString(binding.route) && params.route == null) params.route = pickString(binding.route);
    const dbContent = {
      title: firstString([effectiveContent.emptyStateTitle, heading, data.label]),
      subtitle: firstString([effectiveContent.subheading, effectiveContent.subtitle]),
    };
    const frame = placeBlock(cursor, 420);
    elements.push(dataBoundElement(nextId('dataBound'), frame, pushZ(), dbContent, { source: pickString(binding.source), params }));
  }

  // Compute the final frame height from the laid-out content.
  const authoredFrameHeight = resolveAuthoredFrameHeight(style, layout);
  const frameHeight = Math.max(cursorFrameHeight(cursor), authoredFrameHeight || 0);

  // 9) Full-frame background image (rendered behind everything, z=0).
  if (style.backgroundImage != null && isUsableAsset(style.backgroundImage)) {
    elements.unshift(
      imageElement(
        nextId('bg'),
        { x: 0, y: 0, width: cursor.frameWidth, height: frameHeight },
        0,
        style.backgroundImage,
        { objectFit: 'cover', objectPosition: 'center', radius: 0, opacity: 1 },
        'Background',
      ),
    );
  }

  // Frame style + lossless provenance.
  const frameStyle = deriveFrameStyle(style);
  const model = {
    width: cursor.frameWidth,
    height: frameHeight,
    ...frameStyle,
    elements,
    // Frame layout classification (Req 4.3/4.4/5.1), derived deterministically
    // from the descriptor `layout.width` + `style.padding`. Element placement is
    // unchanged; `normalizeCanvas` clamps these three fields.
    fullBleed: layout.width === 'full',
    contentInset: parsePadding(style.padding).left,
    contentMaxWidth: REFERENCE_FRAME_WIDTH,
    source: {
      type: sectionType,
      preserved: {
        content: deepClone(content),
        style: deepClone(style),
        layout: deepClone(layout),
      },
    },
  };

  return normalizeCanvas(model);
}

export default buildSectionCanvas;
